import { useCallback, useEffect, useState } from 'react';
import { FileText, Plus, Trash2, Upload, Pencil, ExternalLink } from 'lucide-react';
import Button from '../ui/Button';
import CertificationTypeCombobox from './CertificationTypeCombobox';
import CredentialStatusBadge from '../spray/CredentialStatusBadge';
import { useAuth } from '../../context/AuthContext';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import {
  listCertificationTypes,
  listMyCertifications,
  listUserCertifications,
  createCertification,
  updateCertification,
  deleteCertification,
  openCertificationDocument,
  type CertificationModuleFilter,
  type StaffCertificationType,
  type UserCertification,
} from '../../services/staffCertification';

interface CertificationsListProps {
  /**
   * Target user. Omit (or pass the logged-in user's id) to manage the current
   * user's own records via the self-service endpoints. When this is another
   * user's id the component uses the HR endpoints (server enforces the HR
   * license). Designed so PR3 can embed it in the unified employee profile.
   */
  userId?: number;
  /** Show the add / edit / delete controls. Defaults to true. */
  canManage?: boolean;
  /** Type-catalog filter for the "add" dropdown. Defaults to 'general'. */
  typeModuleFilter?: CertificationModuleFilter;
  className?: string;
}

interface EditingState {
  id: number | null; // null = new record
  certification_type_id: number | '';
  credential_number: string;
  issuer: string;
  issued_date: string;
  expiry_date: string;
  notes: string;
  document: File | null;
}

const emptyForm = (): EditingState => ({
  id: null,
  certification_type_id: '',
  credential_number: '',
  issuer: '',
  issued_date: '',
  expiry_date: '',
  notes: '',
  document: null,
});

function CertificationsList({
  userId,
  canManage = true,
  typeModuleFilter = 'general',
  className,
}: CertificationsListProps) {
  const { user } = useAuth();
  const orgTz = useOrgTimezone();
  // Creating new certification TYPES is HR-only (the backend enforces the
  // timesheet.hr seat on POST /certifications/types). Non-HR users can still
  // pick from the existing catalog, but not add new types inline.
  const { hasAccess: canManageTypes } = useModuleLicense('timesheet', 'hr');

  // Treat an omitted userId, or one matching the logged-in user, as "self".
  const isSelf = userId == null || userId === user?.id;

  const [types, setTypes] = useState<StaffCertificationType[]>([]);
  const [records, setRecords] = useState<UserCertification[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  const viewDocument = async (c: UserCertification) => {
    setDocError(null);
    try {
      await openCertificationDocument(c);
    } catch {
      setDocError(`Could not open ${c.document_original_name || 'the document'}. Try again.`);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const recordsPromise = isSelf
        ? listMyCertifications()
        : listUserCertifications(userId as number);
      const [typesRes, recsRes] = await Promise.all([
        listCertificationTypes({ module: typeModuleFilter }),
        recordsPromise,
      ]);
      if (typesRes.success) setTypes(typesRes.data);
      if (recsRes.success) setRecords(recsRes.data);
    } finally {
      setLoading(false);
    }
  }, [isSelf, userId, typeModuleFilter]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing(emptyForm());
    setError(null);
  };

  const startEdit = (c: UserCertification) => {
    setEditing({
      id: c.id,
      certification_type_id: c.certification_type_id,
      credential_number: c.credential_number || '',
      issuer: c.issuer || '',
      issued_date: c.issued_date || '',
      expiry_date: c.expiry_date || '',
      notes: c.notes || '',
      document: null,
    });
    setError(null);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.certification_type_id) {
      setError('Please select a certification type.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing.id == null) {
        const res = await createCertification({
          certification_type_id: Number(editing.certification_type_id),
          credential_number: editing.credential_number || undefined,
          issuer: editing.issuer || undefined,
          issued_date: editing.issued_date || undefined,
          expiry_date: editing.expiry_date || undefined,
          notes: editing.notes || undefined,
          document: editing.document,
          // Record on the target user's behalf when not self (HR only).
          user_id: isSelf ? undefined : (userId as number),
        });
        if (!res.success) throw new Error('Save failed');
      } else {
        const res = await updateCertification(editing.id, {
          credential_number: editing.credential_number,
          issuer: editing.issuer,
          issued_date: editing.issued_date,
          expiry_date: editing.expiry_date,
          notes: editing.notes,
          document: editing.document,
        });
        if (!res.success) throw new Error('Save failed');
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTypeCreated = (t: StaffCertificationType) => {
    setTypes(prev => (prev.some(p => p.id === t.id) ? prev : [...prev, t]));
  };

  const remove = async (id: number) => {
    if (!confirm('Delete this certification record?')) return;
    await deleteCertification(id);
    await load();
  };

  return (
    <div className={className}>
      {canManage && (
        <div className="mb-4 flex justify-end">
          <Button size="sm" onClick={startNew}>
            <Plus className="h-4 w-4 mr-1" /> Add certification
          </Button>
        </div>
      )}

      {docError && <p className="mb-3 text-sm text-rose-600">{docError}</p>}

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : records.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-xl p-10 text-center">
          <FileText className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-600">No certifications recorded yet.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {records.map(c => (
            <div key={c.id} className="border border-neutral-200 bg-white rounded-xl p-4 flex items-start justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-semibold text-neutral-900">{c.type_name}</h3>
                  <CredentialStatusBadge status={c.status} />
                  {c.type_category && (
                    <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">{c.type_category}</span>
                  )}
                  {c.type_is_required && (
                    <span className="text-xs text-neutral-500">(required)</span>
                  )}
                </div>
                <div className="text-sm text-neutral-600 space-y-0.5">
                  {c.credential_number && <p>Number: {c.credential_number}</p>}
                  {c.issuer && <p>Issuer: {c.issuer}</p>}
                  {c.issued_date && <p>Issued: {formatLocalDateInTz(c.issued_date, orgTz)}</p>}
                  {c.expiry_date && <p>Expires: {formatLocalDateInTz(c.expiry_date, orgTz)}</p>}
                  {c.notes && <p className="text-neutral-500 italic">{c.notes}</p>}
                  {c.has_document && (
                    <button
                      type="button"
                      onClick={() => viewDocument(c)}
                      className="text-primary-600 hover:underline inline-flex items-center gap-1 text-xs text-left break-all"
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      {c.document_original_name || 'View document'}
                    </button>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex gap-1">
                  <button onClick={() => startEdit(c)} className="p-1.5 text-neutral-500 hover:text-primary-600" aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(c.id)} className="p-1.5 text-neutral-500 hover:text-rose-600" aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CertificationFormModal
          state={editing}
          setState={setEditing}
          types={types}
          onClose={() => setEditing(null)}
          onSave={save}
          onTypeCreated={handleTypeCreated}
          canManageTypes={canManageTypes}
          saving={saving}
          error={error}
          isNew={editing.id == null}
        />
      )}
    </div>
  );
}

interface ModalProps {
  state: EditingState;
  setState: (s: EditingState) => void;
  types: StaffCertificationType[];
  onClose: () => void;
  onSave: () => void;
  onTypeCreated: (t: StaffCertificationType) => void;
  canManageTypes: boolean;
  saving: boolean;
  error: string | null;
  isNew: boolean;
}

function CertificationFormModal({ state, setState, types, onClose, onSave, onTypeCreated, canManageTypes, saving, error, isNew }: ModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <header className="px-5 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold">{isNew ? 'Add certification' : 'Edit certification'}</h2>
        </header>
        <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {isNew && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Certification type</label>
              <CertificationTypeCombobox
                types={types}
                value={state.certification_type_id}
                onChange={id => setState({ ...state, certification_type_id: id })}
                onTypeCreated={onTypeCreated}
                canAddType={canManageTypes}
              />
              {canManageTypes && (
                <p className="mt-1 text-xs text-neutral-500">
                  Can't find it? Type a new name (e.g. "Forklift licence") and click "Add".
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Credential number</label>
              <input
                type="text"
                value={state.credential_number}
                onChange={e => setState({ ...state, credential_number: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Issuer</label>
              <input
                type="text"
                value={state.issuer}
                onChange={e => setState({ ...state, issuer: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Issued date</label>
              <input
                type="date"
                value={state.issued_date}
                onChange={e => setState({ ...state, issued_date: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry date</label>
              <input
                type="date"
                value={state.expiry_date}
                onChange={e => setState({ ...state, expiry_date: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea
              value={state.notes}
              onChange={e => setState({ ...state, notes: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Document (PDF or image, max 10 MB)</label>
            <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-md bg-neutral-100 border border-neutral-300 text-sm hover:bg-neutral-200">
              <Upload className="h-4 w-4" />
              {state.document ? state.document.name : 'Choose file'}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                onChange={e => setState({ ...state, document: e.target.files?.[0] || null })}
                className="hidden"
              />
            </label>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} isLoading={saving}>Save</Button>
        </footer>
      </div>
    </div>
  );
}

export default CertificationsList;
